import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { ImportDialog } from '../features/ImportDialog';
import type { Account, AdminUser, Enterprise, Page } from '../lib/types';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { suggestAccountCode } from '../lib/account-code';
import { timezoneHint } from '../lib/timezones';
import {
  HEALTH_LABEL,
  HEALTH_TONE,
  IMPORTANCE_TONE,
  formatDate,
  titleCase,
} from '../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNotice,
  Input,
  Loading,
  Button,
  Dialog,
  Field,
  PageHeader,
  useToast,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

/**
 * Account list.
 *
 * What a caller sees here *is* their permission scope — the server filters in
 * SQL, so the count in the header is the truth about their access rather than a
 * page of results that happens to be short.
 */
export function AccountsPage() {
  const { me } = useSession();
  const [q, setQ] = useState('');
  const [importance, setImportance] = useState('');
  const [health, setHealth] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('name');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const query = useQuery({
    queryKey: ['accounts', { q, importance, health, status, sort }],
    queryFn: () =>
      api.get<Page<Account>>('/accounts', {
        q: q || undefined,
        importance: importance || undefined,
        health: health || undefined,
        status: status || undefined,
        sort,
        limit: 200,
      }),
  });

  const accounts = query.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Accounts"
        description={
          me.accountScope.unbounded
            ? 'Every account on the platform.'
            : `The ${me.accountScope.accountCount} accounts you are assigned to or cover. Access is enforced server-side.`
        }
        actions={
          me.capabilities.canCreateAccounts && (
            <>
              <Button size="sm" variant="secondary" onClick={() => setImporting(true)}>
                Import
              </Button>
              <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                New account
              </Button>
            </>
          )
        }
        meta={
          <div className="flex flex-wrap items-end gap-2">
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search accounts or enterprises"
              className="w-56"
            />
            <Select
              value={importance}
              onChange={(event) => setImportance(event.target.value)}
              className="w-36"
            >
              <option value="">Any importance</option>
              <option value="strategic">Strategic</option>
              <option value="high">High</option>
              <option value="standard">Standard</option>
              <option value="project">Project</option>
            </Select>
            <Select
              value={health}
              onChange={(event) => setHealth(event.target.value)}
              className="w-32"
            >
              <option value="">Any health</option>
              <option value="red">Red</option>
              <option value="yellow">Yellow</option>
              <option value="green">Green</option>
              <option value="unknown">Not rated</option>
            </Select>
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="w-36"
            >
              <option value="">Any status</option>
              <option value="active">Active</option>
              <option value="onboarding">Onboarding</option>
              <option value="pilot">Pilot</option>
              <option value="paused">Paused</option>
              <option value="closed">Closed</option>
            </Select>
            <Select value={sort} onChange={(event) => setSort(event.target.value)} className="w-40">
              <option value="name">Sort by name</option>
              <option value="importance">Sort by importance</option>
              <option value="health">Sort by health</option>
              <option value="updated">Recently updated</option>
            </Select>
          </div>
        }
      />
      {importing && (
        <ImportDialog
          entity="accounts"
          label="accounts"
          invalidate={[['accounts'], ['enterprises']]}
          onClose={() => setImporting(false)}
        />
      )}

      {query.isLoading && <Loading label="Loading accounts" />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {query.data && accounts.length === 0 && (
        <EmptyState
          title="No accounts match"
          description={
            q || importance || health || status
              ? 'Try relaxing the filters.'
              : 'You are not assigned to any accounts yet. Ask CS Leadership for an assignment.'
          }
        />
      )}

      {accounts.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th>Enterprise</Th>
                <Th>Importance</Th>
                <Th>Health</Th>
                <Th>Lifecycle</Th>
                <Th>Primary CSM</Th>
                <Th>Note</Th>
                <Th align="right">Updated</Th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <Tr key={account.id}>
                  <Td>
                    <Link
                      to={`/accounts/${account.id}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {account.parentAccountId && <span className="text-muted">↳ </span>}
                      {account.name}
                    </Link>
                    {account.accountCode && (
                      <span className="mt-0.5 block text-xs text-muted">{account.accountCode}</span>
                    )}
                  </Td>
                  <Td className="text-ink-2">{account.enterpriseName}</Td>
                  <Td>
                    <Badge
                      tone={IMPORTANCE_TONE[account.importance]}
                      dot={account.importance === 'strategic'}
                    >
                      {titleCase(account.importance)}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge tone={HEALTH_TONE[account.healthCurrent]}>
                      {HEALTH_LABEL[account.healthCurrent]}
                    </Badge>
                  </Td>
                  <Td className="text-ink-2">{titleCase(account.lifecycle)}</Td>
                  <Td className="text-ink-2">{account.primaryCsmName}</Td>
                  <Td className="max-w-72 text-xs text-ink-2">
                    <span className="line-clamp-2">{account.currentAccountNote ?? '—'}</span>
                  </Td>
                  <Td align="right" className="text-xs whitespace-nowrap text-muted">
                    {formatDate(account.updatedAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {creating && <CreateAccountDialog onClose={() => setCreating(false)} />}
    </>
  );
}


/**
 * §6.2 — a new account.
 *
 * `importance` is deliberately absent: §6.2 reserves it to Leadership and Admin
 * and gives it its own audited endpoint, so it is set from the account screen
 * after creation rather than buried in a create form. Health is likewise absent
 * — §23 requires a reason for any override, which this form has no place for.
 */
function CreateAccountDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [enterpriseId, setEnterpriseId] = useState('');
  const [name, setName] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [codeEdited, setCodeEdited] = useState(false);
  const [parentAccountId, setParentAccountId] = useState('');
  const [primaryCsmUserId, setPrimaryCsmUserId] = useState('');
  const [status, setStatus] = useState('onboarding');
  const [lifecycle, setLifecycle] = useState('onboarding');
  const [timezone, setTimezone] = useState('Asia/Kolkata');

  const enterprises = useQuery({
    queryKey: ['enterprises'],
    queryFn: () => api.get<Page<Enterprise>>('/enterprises', { limit: 200 }),
  });
  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<Page<AdminUser>>('/admin/users'),
  });
  const accounts = useQuery({
    queryKey: ['accounts', { sort: 'name' }],
    queryFn: () => api.get<Page<Account>>('/accounts', { sort: 'name', limit: 200 }),
  });

  // A sub-account must sit under the same enterprise as its parent (§5), so the
  // parent list narrows as soon as an enterprise is chosen.
  const parentOptions = (accounts.data?.data ?? []).filter(
    (a) => !enterpriseId || a.enterpriseId === enterpriseId,
  );

  // The code follows the name until someone types their own, and follows it
  // again if they clear the field. A sub-account extends its parent's code; a
  // top-level one extends the enterprise's.
  const enterpriseCode = (enterprises.data?.data ?? []).find((e) => e.id === enterpriseId)?.code;
  const parentCode = parentOptions.find((a) => a.id === parentAccountId)?.accountCode;
  const suggestedCode = suggestAccountCode(name, parentCode || enterpriseCode || '');

  useEffect(() => {
    if (!codeEdited) setAccountCode(suggestedCode);
  }, [codeEdited, suggestedCode]);

  const create = useMutation({
    mutationFn: () =>
      api.post('/accounts', {
        enterpriseId,
        name,
        accountCode: accountCode || null,
        parentAccountId: parentAccountId || null,
        primaryCsmUserId,
        status,
        lifecycle,
        timezone,
      }),
    onSuccess: () => {
      toast('Account created');
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['enterprises'] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="New account"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={create.isPending || !name.trim() || !enterpriseId || !primaryCsmUserId}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Create account'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {create.error && <ErrorNotice error={create.error} />}

        <Field label="Enterprise" required>
          <Select value={enterpriseId} onChange={(event) => setEnterpriseId(event.target.value)}>
            <option value="">Select an enterprise…</option>
            {(enterprises.data?.data ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.code} — {e.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Account name" required>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="J&amp;J MedTech India"
            />
          </Field>
          <Field
            label="Account code"
            hint={
              codeEdited && suggestedCode && accountCode !== suggestedCode ? (
                // Typing here stops the field following the name — including
                // clearing it, which is a legitimate choice since the code is
                // optional. This is the way back.
                <button
                  type="button"
                  className="text-accent underline underline-offset-2"
                  onClick={() => {
                    setCodeEdited(false);
                    setAccountCode(suggestedCode);
                  }}
                >
                  Use {suggestedCode}
                </button>
              ) : (
                'Follows the account name.'
              )
            }
          >
            <Input
              value={accountCode}
              onChange={(event) => {
                setCodeEdited(true);
                setAccountCode(event.target.value.toUpperCase());
              }}
              placeholder="JNJ-MT-IN"
            />
          </Field>
        </div>

        <Field
          label="Parent account"
          hint="Only for a sub-account. Permissions inherit downward from the parent."
        >
          <Select
            value={parentAccountId}
            onChange={(event) => setParentAccountId(event.target.value)}
            disabled={!enterpriseId}
          >
            <option value="">None — a top-level account</option>
            {parentOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Primary CSM" required>
          <Select
            value={primaryCsmUserId}
            onChange={(event) => setPrimaryCsmUserId(event.target.value)}
          >
            <option value="">Select an owner…</option>
            {(users.data?.data ?? [])
              .filter((u) => u.status === 'active')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                  {u.title ? ` — ${u.title}` : ''}
                </option>
              ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <Select value={status} onChange={(event) => setStatus(event.target.value)}>
              {['onboarding', 'active', 'pilot', 'paused', 'closed'].map((s) => (
                <option key={s} value={s}>
                  {titleCase(s)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Lifecycle">
            <Select value={lifecycle} onChange={(event) => setLifecycle(event.target.value)}>
              {['onboarding', 'active', 'sustainment', 'at_risk', 'paused', 'closed'].map((s) => (
                <option key={s} value={s}>
                  {titleCase(s)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Timezone" hint={timezoneHint(timezone, 'Drives business hours.')}>
          <TimezoneSelect value={timezone} onChange={setTimezone} />
        </Field>
      </div>
    </Dialog>
  );
}
